"use client";

import React, { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { usePresets } from "@lib/api-hooks/usePresets/usePresets";
import { PRESET_STATUS_READY_TO_USE } from "@/constants/constants";
import { BookOpenIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "@tracify/ui/button";
import { useRouter } from "next/navigation";
import { useFeatureFlags } from "@lib/hooks/useFeatureFlags";
import { useAttributionPresetState } from "@/lib/hooks/url-state";
import { setLastAttributionPresetForOrg } from "@lib/util-functions/last-attribution-preset-storage";

const AttributionMenu = () => {
  const { selectedOrganisation } = useSelectedStores();
  const { data: presets = [] } = usePresets(selectedOrganisation ?? "", {
    enabled: Boolean(selectedOrganisation),
  });
  const router = useRouter();

  // Show all presets if at least one site has READY, PREFILLING or REPROCESSING status
  const presetsToShow = useMemo(() => {
    return presets?.filter((preset) => {
      // Check if at least one site has READY, PREFILLING or REPROCESSING status
      return Object.values(preset.statusPerSite || {}).some((status) => {
        return PRESET_STATUS_READY_TO_USE.includes(status);
      });
    });
  }, [presets]);

  const { attributionPreset, setAttributionPreset } =
    useAttributionPresetState();

  const onChangePreset = (value: string) => {
    const preset = presetsToShow?.find((el) => el.presetId === value);
    if (preset) {
      setAttributionPreset(value);
      if (selectedOrganisation) {
        setLastAttributionPresetForOrg(selectedOrganisation, value);
      }
    }
  };

  const { getFlag } = useFeatureFlags();
  const isTracifyV2PresetsEnabled = getFlag("tracify_v2_presets")?.enabled;

  return (
    <Select
      value={attributionPreset ?? undefined}
      onValueChange={onChangePreset}
    >
      <SelectTrigger id="attribution-preset-select">
        <SelectValue placeholder="Choose a preset" />
      </SelectTrigger>
      <SelectContent align="end">
        <SelectGroup>
          <SelectLabel>Presets</SelectLabel>
          {presetsToShow?.map((option) => (
            <SelectItem key={option.presetId} value={option.presetId}>
              {option.name}
            </SelectItem>
          ))}
        </SelectGroup>
        {isTracifyV2PresetsEnabled ? (
          <SelectGroup>
            <Button
              size="sm"
              variant="ghost"
              className="w-full mt-0.5 hover:bg-primary hover:text-primary-foreground"
              onClick={(e) => {
                router.push("/settings/presets");
              }}
            >
              <PlusIcon className="w-4 h-4 mr-2" /> Create new preset
            </Button>
          </SelectGroup>
        ) : null}
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel className="py-3 -m-1 font-normal rounded-t-none bg-accent text-accent-foreground flex items-center">
            <BookOpenIcon className="h-4 w-4" />
            <a
              href="https://knowledge.tracify.ai/en/articles/529776-understanding-presets-in-tracify"
              target="_blank"
              rel="noopener noreferrer"
              className="underline ml-1 text-xs"
            >
              Learn more about presets
            </a>
          </SelectLabel>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export default AttributionMenu;
